'use client'

import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { DialogTitle } from '@/ui/atoms/dialog/Dialog'
import { MultiPanelDialog } from '@/features/dialogs/common/components/MultiPanelDialog'
import { DialogPanel } from '@/features/dialogs/common/components/DialogPanel'
import { TransactionOverviewDetailsItem } from '@/features/dialogs/common/components/transaction-overview/TransactionOverviewDetailsItem'
import type { ActionHandlerState } from '@/features/actions/logic/types'
import { getTokenImage } from '@/ui/assets/tokens'
import { Slider } from '@/ui/atoms/slider/Slider'
import { Dialog, DialogContent } from '@/ui/atoms/dialog/Dialog'
import { Typography } from '@/ui/atoms/typography/Typography'
import { ActionRow } from '@/features/actions/components/action-row/ActionRow'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { assets } from '@/ui/assets'
import { Token } from '@/domain/types/Token'
import { BigBlocksBanner } from '@/ui/molecules/big-blocks-banner/BigBlocksBanner'
import { TransactionErrorPanel } from '@/ui/molecules/transaction-error-panel/TransactionErrorPanel'
import { TimeoutErrorDisplay } from '@/features/actions/components/TimeoutErrorDisplay'
import Image from 'next/image'
import { getHealthStatus, formatHealthFactorValue } from '@/domain/common/health-factor'
import { cn } from '@/ui/utils/style'
import { ArrowRight, ChevronDown, Plus, Loader2 } from 'lucide-react'
import { useState, useEffect } from 'react'
import { normalizeDecimalInput } from '@/utils/decimalInput'
import { isUserRejection } from '@/lib/errors'
import BigNumber from 'bignumber.js'
import type { SubAccountOption } from '../logic/useETransferDialog'

interface ETransferViewProps {
  assetSymbol: string
  logoURI?: string
  transferAmount: string
  setTransferAmount: (amount: string) => void
  maxTransferable: number
  selectedDestination: SubAccountOption | null
  setSelectedDestination: (dest: SubAccountOption | null) => void
  destinations: SubAccountOption[]
  isDestinationsLoading: boolean
  sourceSubAccountLabel: string
  transferValueUSD: number
  /** Token for action row display */
  token: Token | undefined
  /** Whether amount is still debouncing */
  isDebouncing: boolean
  /** Whether simulation is in progress */
  isSimulating: boolean
  /** Whether transaction write is pending */
  isWriting: boolean
  /** Whether waiting for transaction receipt */
  isWaiting: boolean
  /** Whether transaction is confirmed */
  isConfirmed: boolean
  /** Whether we have a valid simulation ready to execute */
  hasSimulationData: boolean
  /** Whether the transfer can execute (validation passed) */
  canTransfer: boolean
  /** Combined display error (filtered for user rejections) */
  displayError: Error | null
  /** Raw simulation error (for simulation warning hint) */
  simulationError: Error | null
  /** Execute the transfer */
  onExecute: () => void
  /** Current health factor of the source position */
  currentSourceHF: number
  /** Predicted health factor after transfer */
  newSourceHF: number
  /** Whether HF simulation is in progress */
  isSimulatingSourceHF: boolean
  /** Whether source position has debt (HF is relevant) */
  sourceHasDebt: boolean
  /** Whether the action has timed out */
  isTimedOut: boolean
}

export function ETransferView({
  assetSymbol,
  logoURI,
  transferAmount,
  setTransferAmount,
  maxTransferable,
  selectedDestination,
  setSelectedDestination,
  destinations,
  isDestinationsLoading,
  sourceSubAccountLabel,
  transferValueUSD,
  token,
  isDebouncing,
  isSimulating,
  isWriting,
  isWaiting,
  hasSimulationData,
  canTransfer,
  displayError,
  simulationError,
  onExecute,
  currentSourceHF,
  newSourceHF,
  isSimulatingSourceHF,
  sourceHasDebt,
  isTimedOut,
}: ETransferViewProps) {
  const tokenImage = logoURI || getTokenImage(TokenSymbol(assetSymbol))

  // Local state for input to prevent focus loss
  const [inputValue, setInputValue] = useState(transferAmount)
  const [isInputFocused, setIsInputFocused] = useState(false)
  const [sliderValue, setSliderValue] = useState(0)
  const [selectorOpen, setSelectorOpen] = useState(false)

  // Sync local state when parent state changes
  useEffect(() => {
    if (!isInputFocused) {
      setInputValue(transferAmount)
    }
  }, [transferAmount, isInputFocused])

  // Sync slider with transfer amount
  useEffect(() => {
    const amount = Number(transferAmount) || 0
    if (maxTransferable > 0) {
      setSliderValue((amount / maxTransferable) * 100)
    }
  }, [transferAmount, maxTransferable])

  const transferAmountNum = Number(transferAmount) || 0
  const exceedsBalance = transferAmountNum > maxTransferable

  // Handle slider change — BigNumber avoids scientific notation for small balances
  const handleSliderChange = (values: number[]) => {
    const percentage = values[0] ?? 0
    setSliderValue(percentage)
    const amount = new BigNumber(percentage).dividedBy(100).multipliedBy(maxTransferable)
    setTransferAmount(amount.toFixed())
  }

  const handleSelectDestination = (dest: SubAccountOption) => {
    setSelectedDestination(dest)
    setSelectorOpen(false)
  }

  // Determine action status (matches PositionAssetSwapDialog pattern)
  const executeStatus: ActionHandlerState['status'] = (() => {
    if (isWriting || isWaiting) return 'loading'
    if (isDebouncing || isSimulating) return 'loading'
    if (!hasSimulationData) return 'disabled'
    return 'ready'
  })()

  const actionStatus = isDebouncing || isSimulating ? 'loading' : canTransfer ? executeStatus : 'disabled'

  return (
    <MultiPanelDialog>
      <DialogTitle className="font-semibold text-white text-xl">Transfer Between Positions</DialogTitle>

      <DialogPanel>
        <div className="flex flex-col gap-4">
          {/* From section */}
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
            <div className="flex items-center gap-3">
              {tokenImage && (
                <Image src={tokenImage} alt={assetSymbol} width={32} height={32} className="rounded-full" />
              )}
              <div className="flex flex-col">
                <span className="text-muted-foreground text-xs">From</span>
                <span className="font-medium text-sm text-white">{sourceSubAccountLabel}</span>
                <span className="text-muted-foreground text-xs">
                  {maxTransferable.toLocaleString(undefined, { maximumFractionDigits: 6 })} {assetSymbol}
                </span>
              </div>
            </div>
          </div>

          {/* Arrow */}
          <div className="-my-2 flex justify-center">
            <div className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/5">
              <ArrowRight className="h-4 w-4 rotate-90 text-muted-foreground" />
            </div>
          </div>

          {/* To Section — entire card is clickable, opens dropdown dialog */}
          <div className="overflow-hidden rounded-xl border border-primary/10 bg-primary/[0.02]">
            <Dialog open={selectorOpen} onOpenChange={setSelectorOpen}>
              <button
                type="button"
                className="flex w-full cursor-pointer items-center justify-between rounded-xl p-4 transition-colors hover:bg-primary/[0.04]"
                onClick={() => setSelectorOpen(true)}
              >
                {/* Left: Destination info */}
                <div className="flex items-center gap-3">
                  {selectedDestination ? (
                    <>
                      <div className="flex h-8 w-8 items-center justify-center rounded-full border border-primary/30 bg-primary/10">
                        {selectedDestination.isNew ? (
                          <Plus className="h-4 w-4 text-primary" />
                        ) : (
                          <span className="font-bold text-primary text-xs">#{selectedDestination.id}</span>
                        )}
                      </div>
                      <div className="flex flex-col items-start gap-0.5">
                        <span className="text-muted-foreground text-xs">Transfer to</span>
                        <span className="font-medium text-sm text-white">{selectedDestination.label}</span>
                        {selectedDestination.isNew ? (
                          <span className="text-primary/70 text-xs">New sub-account</span>
                        ) : selectedDestination.debtSymbols.length > 0 ? (
                          <div className="flex items-center gap-1.5">
                            <span className="text-muted-foreground text-xs">Debt:</span>
                            {selectedDestination.debtSymbols.map((symbol) => (
                              <div key={symbol} className="flex items-center gap-1">
                                <TokenIcon token={{ symbol: TokenSymbol(symbol) }} className="h-3.5 w-3.5" />
                                <span className="text-muted-foreground text-xs">{symbol}</span>
                              </div>
                            ))}
                            {selectedDestination.marketName && (
                              <span className="text-muted-foreground text-xs">{selectedDestination.marketName}</span>
                            )}
                            {selectedDestination.healthFactor !== null && (
                              <span
                                className={cn(
                                  'text-xs',
                                  selectedDestination.healthFactor >= 3
                                    ? 'text-teal-400'
                                    : selectedDestination.healthFactor >= 2
                                      ? 'text-amber-400'
                                      : 'text-red-400',
                                )}
                              >
                                HF{' '}
                                {selectedDestination.healthFactor >= 4
                                  ? '4+'
                                  : selectedDestination.healthFactor.toFixed(2)}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-xs">No debt</span>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/5">
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="flex flex-col items-start">
                        <span className="text-muted-foreground text-xs">Transfer to</span>
                        <span className="text-muted-foreground text-sm">Select position</span>
                      </div>
                    </>
                  )}
                </div>
                {/* Right: Chevron */}
                <ChevronDown className="h-4 w-4 shrink-0 text-white/50" />
              </button>

              <DialogContent
                className="md:data-[state=closed]:slide-out-to-top-[16px] md:data-[state=open]:slide-in-from-top-[16px] border border-border bg-background px-6 pt-6 md:top-24 md:min-w-0 md:max-w-md md:pb-2"
                aria-describedby={undefined}
                contentVerticalPosition="top"
                overlayClassName="z-[240]"
                closeClassName="top-6"
              >
                <div className="mb-4">
                  <DialogTitle className="mb-2">
                    <Typography variant="p" className="font-bold text-lg" gradient>
                      Select destination
                    </Typography>
                  </DialogTitle>
                </div>

                <div className="max-h-80 space-y-1 overflow-y-auto pb-4">
                  {isDestinationsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : destinations.length === 0 ? (
                    <div className="p-4 text-center">
                      <Typography variant="prompt">No other positions available</Typography>
                    </div>
                  ) : (
                    destinations.map((dest) => (
                      <button
                        key={dest.id}
                        type="button"
                        className={cn(
                          'flex w-full items-center gap-3 rounded-lg p-3 transition-colors hover:bg-muted',
                          selectedDestination?.id === dest.id && 'bg-muted',
                        )}
                        onClick={() => handleSelectDestination(dest)}
                      >
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5">
                          {dest.isNew ? (
                            <Plus className="h-4 w-4 text-primary" />
                          ) : (
                            <span className="font-bold text-white/70 text-xs">#{dest.id}</span>
                          )}
                        </div>
                        <div className="flex flex-1 flex-col items-start gap-0.5">
                          <span className="font-medium text-sm text-white">{dest.label}</span>
                          {dest.isNew ? (
                            <span className="text-primary/70 text-xs">Creates a new sub-account</span>
                          ) : dest.debtSymbols.length > 0 ? (
                            <div className="flex w-full items-center justify-between">
                              <div className="flex items-center gap-1.5">
                                <span className="text-muted-foreground text-xs">Debt:</span>
                                {dest.debtSymbols.map((symbol) => (
                                  <div key={symbol} className="flex items-center gap-1">
                                    <TokenIcon token={{ symbol: TokenSymbol(symbol) }} className="h-4 w-4" />
                                    <span className="text-muted-foreground text-xs">{symbol}</span>
                                  </div>
                                ))}
                                {dest.marketName && (
                                  <span className="text-muted-foreground text-xs">{dest.marketName}</span>
                                )}
                                {dest.healthFactor !== null && (
                                  <span
                                    className={cn(
                                      'text-xs',
                                      dest.healthFactor >= 3
                                        ? 'text-teal-400'
                                        : dest.healthFactor >= 2
                                          ? 'text-amber-400'
                                          : 'text-red-400',
                                    )}
                                  >
                                    HF {dest.healthFactor >= 4 ? '4+' : dest.healthFactor.toFixed(2)}
                                  </span>
                                )}
                              </div>
                              <span className="text-muted-foreground text-xs">
                                ${dest.totalDebtUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                              </span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-xs">No debt</span>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Transfer Amount Input */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <label className="text-muted-foreground text-xs">Amount to Transfer</label>
              <button
                type="button"
                onClick={() => setTransferAmount(new BigNumber(maxTransferable).toFixed())}
                className="text-primary text-xs hover:underline"
              >
                Max: {maxTransferable.toLocaleString(undefined, { maximumFractionDigits: 6 })} {assetSymbol}
              </button>
            </div>
            <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] p-3">
              {tokenImage && (
                <Image src={tokenImage} alt={assetSymbol} width={32} height={32} className="rounded-full" />
              )}
              <input
                type="text"
                inputMode="decimal"
                value={inputValue}
                onChange={(e) => {
                  const normalized = normalizeDecimalInput(e.target.value)
                  if (normalized !== null) {
                    setInputValue(normalized)
                    setTransferAmount(normalized || '0')
                  }
                }}
                onFocus={() => setIsInputFocused(true)}
                onBlur={() => {
                  setIsInputFocused(false)
                  setInputValue(transferAmount)
                }}
                className="flex-1 bg-transparent text-lg text-white outline-none placeholder:text-white/30"
                placeholder="0.00"
              />
              <span className="text-muted-foreground">{assetSymbol}</span>
            </div>
            {transferAmountNum > 0 && (
              <div className="text-right text-muted-foreground text-xs">
                ${transferValueUSD.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            )}
          </div>

          {/* Slider */}
          <div className="flex flex-col gap-2">
            <Slider value={[sliderValue]} max={100} min={0} step={0.1} onValueChange={handleSliderChange} />
            <div className="flex items-center justify-between text-white/30 text-xs">
              <span>0%</span>
              <span>100% (Max)</span>
            </div>
          </div>

          {/* Transaction Summary */}
          {transferAmountNum > 0 && selectedDestination && !exceedsBalance && (
            <div className="mt-2 flex flex-col gap-2">
              <TransactionOverviewDetailsItem label="Transfer amount">
                {transferAmountNum.toLocaleString(undefined, { maximumFractionDigits: 6 })} {assetSymbol}
              </TransactionOverviewDetailsItem>
              <TransactionOverviewDetailsItem label="From">{sourceSubAccountLabel}</TransactionOverviewDetailsItem>
              <TransactionOverviewDetailsItem label="To">{selectedDestination.label}</TransactionOverviewDetailsItem>
              {sourceHasDebt && (
                <>
                  <TransactionOverviewDetailsItem label="Current Health Factor">
                    {isSimulatingSourceHF ? (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    ) : (
                      <div className="flex items-center gap-2">
                        {(() => {
                          const status = getHealthStatus(currentSourceHF)
                          return (
                            <>
                              <status.icon className={cn('h-4 w-4', status.color)} />
                              <span className={cn('font-semibold', status.color)}>
                                {formatHealthFactorValue(currentSourceHF)}
                              </span>
                            </>
                          )
                        })()}
                      </div>
                    )}
                  </TransactionOverviewDetailsItem>
                  <TransactionOverviewDetailsItem label="New Health Factor">
                    {isSimulatingSourceHF ? (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    ) : (
                      <div className="flex items-center gap-2">
                        {(() => {
                          const status = getHealthStatus(newSourceHF)
                          return (
                            <>
                              <status.icon className={cn('h-4 w-4', status.color)} />
                              <span className={cn('font-semibold', status.color)}>
                                {formatHealthFactorValue(newSourceHF)}
                              </span>
                            </>
                          )
                        })()}
                      </div>
                    )}
                  </TransactionOverviewDetailsItem>
                </>
              )}
            </div>
          )}

          {/* HF breach warning */}
          {sourceHasDebt && transferAmountNum > 0 && !isSimulatingSourceHF && newSourceHF <= 1.25 && (
            <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-3">
              <p className="text-red-400 text-sm">
                This transfer would put your source position below the safe threshold (HF {'<'} 1.25). Reduce the
                transfer amount.
              </p>
            </div>
          )}

          {/* Error display */}
          <TimeoutErrorDisplay
            isTimedOut={isTimedOut}
            onRetry={onExecute}
            isRetryDisabled={isWriting || isWaiting || !hasSimulationData}
          />
          {!isTimedOut && (
            <TransactionErrorPanel
              error={displayError}
              showSimulationWarning={!!simulationError && !isUserRejection(simulationError)}
            />
          )}

          {/* Insufficient balance warning */}
          {exceedsBalance && (
            <div className="rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-3">
              <p className="text-sm text-yellow-400">
                Insufficient balance. You have {maxTransferable.toLocaleString(undefined, { maximumFractionDigits: 6 })}{' '}
                {assetSymbol}.
              </p>
            </div>
          )}

          {/* No destination selected hint */}
          {!selectedDestination && transferAmountNum > 0 && !exceedsBalance && (
            <div className="rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-3">
              <p className="text-sm text-yellow-400">Please select a destination position.</p>
            </div>
          )}

          <BigBlocksBanner className="mt-4" />

          {/* Execute Button — matches PositionAssetSwapDialog ActionRow pattern */}
          <div className="mt-4 grid grid-cols-[auto_auto_1fr_auto] gap-x-4">
            <ActionRow index={1}>
              <ActionRow.Icon path={assets.actions.exchange} actionStatus={actionStatus} />
              <ActionRow.Title
                icon={token ? <TokenIcon token={token} className="h-6" /> : undefined}
                actionStatus={actionStatus}
              >
                Transfer
              </ActionRow.Title>
              <ActionRow.Description successMessage="Transferred!" actionStatus={actionStatus} variant="compact" />
              <ActionRow.Action onAction={onExecute} status={actionStatus}>
                {isDebouncing || isSimulating ? 'Preparing...' : 'Transfer'}
              </ActionRow.Action>
            </ActionRow>
          </div>
        </div>
      </DialogPanel>
    </MultiPanelDialog>
  )
}
